/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { InvalidParam } from '../tech.model';
import { isNotEmpty, isNotNil } from '../tech.util';
import { ValidationMessageCode } from './tech.validation.messages';

export function fieldEmptyValidator(controlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNotEmpty(control.value)) return null;
    return {
      [ValidationMessageCode.FIELD_EMPTY]: {
        name: controlName,
        value: null,
        reason: ValidationMessageCode.FIELD_EMPTY,
        constraintParameters: [],
      } as InvalidParam,
    };
  };
}

export function fieldInvalidValidator(controlName: string, regex: RegExp): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (regex.test(control.value)) return null;
    return {
      [ValidationMessageCode.FIELD_INVALID]: {
        name: controlName,
        value: null,
        reason: ValidationMessageCode.FIELD_INVALID,
        constraintParameters: [],
      } as InvalidParam,
    };
  };
}

export function fieldLengthValidator(controlName: string, min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    if (isNotEmpty(control.value) && value.length >= min && value.length <= max) return null;
    return {
      [ValidationMessageCode.FIELD_SIZE]: {
        name: controlName,
        value: null,
        reason: ValidationMessageCode.FIELD_SIZE,
        constraintParameters: [
          { name: 'min', value: min.toString() },
          { name: 'max', value: max.toString() },
        ],
      } as InvalidParam,
    };
  };
}

/**
 * A simplified version of validation subgroups of check boxes. Could be extended for more advanced forms.
 * It only looks up direct children of check box groups.
 */
export function checkBoxGroupsEmptyValidator(controlName: string, groupNames: string[]): ValidatorFn {
  return (control: AbstractControl<UntypedFormGroup>): ValidationErrors | null => {
    const group: UntypedFormGroup = control as UntypedFormGroup;
    const found: boolean = groupNames
      .filter((groupName: string) => _existsUntypedFormSubGroup(group, groupName))
      .map((groupName: string) => group.controls[groupName] as UntypedFormGroup)
      .map(_isAtLeastOneChecked)
      .reduce((a: boolean, b: boolean) => a || b);
    if (found) return null;
    return {
      [ValidationMessageCode.FIELD_EMPTY]: {
        name: controlName,
        value: null,
        reason: ValidationMessageCode.FIELD_EMPTY,
        constraintParameters: [],
      } as InvalidParam,
    };
  };
}

export function _existsUntypedFormSubGroup(group: UntypedFormGroup, subGroupName: string): boolean {
  return isNotNil(group.controls[subGroupName]) && group.controls[subGroupName] instanceof UntypedFormGroup;
}

export function _isAtLeastOneChecked(group: UntypedFormGroup): boolean {
  return Object.values(group.value).findIndex((isChecked: boolean) => isChecked) > -1;
}
